import { useEffect, useRef, useState } from "react";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createTokenProvider,
  type DeepLinkController,
  installNativeDeepLinkListeners,
  isNative,
} from "@cntrl/native";
import { Wordmark, Spinner } from "@cntrl/ui";
import { createAppQueryClient } from "./queryClient.js";
import { createCoreApiClient } from "./auth/api.js";
import { bootstrapSession, fetchMe } from "./auth/session.js";
import { useActiveBand } from "./activeBand.js";
import { bandToSwitchTo, takeNotificationBand, useNotificationBandPrompt } from "./notificationBand.js";
import { createSessionSync, type SessionSync } from "./auth/sessionSync.js";
import { AppProvider } from "./appContext.js";
import { BiometricGate } from "./auth/BiometricGate.js";
import { createDeepLinkController } from "./deepLink.js";
import { translateCorePush } from "./pushTarget.js";
import { createAppRouter, type AppRouter } from "../routes/router.js";
import { onNavigation } from "./liveUpdate.js";
import { useClientOutdated } from "./clientVersion.js";
import { ClientOutdatedScreen } from "./ClientOutdatedScreen.js";
import styles from "./Bootstrap.module.scss";

declare global {
  interface Window {
    /** The pre-hydration boot-loader fade-out hook (apps/core/index.html #acLoader). No-op in embed mode, or
     *  absent under `vite dev` before the inline script runs — always call it optionally. */
    acHideLoader?: () => void;
  }
}

interface Services {
  tokenProvider: ReturnType<typeof createTokenProvider>;
  queryClient: ReturnType<typeof createAppQueryClient>;
  api: ReturnType<typeof createCoreApiClient>;
  router: AppRouter;
  deepLink: DeepLinkController;
  sessionSync: SessionSync | undefined;
}

/**
 * The app root. Runs the boot reissue BEFORE the router mounts, so the pre-paint guards read
 * the correct auth state with NO flash of the wrong surface:
 *   1. create the TokenProvider (Keychain on native / in-memory on web), QueryClient, API client (with the
 *      clear-on-401-only middleware), and the router;
 *   2. await bootstrapSession() — native: load Keychain; web: POST /auth/reissue via the httpOnly cookie;
 *   3. only then render the router.
 */
export function Bootstrap() {
  const [ready, setReady] = useState(false);
  const outdated = useClientOutdated((s) => s.remedy);
  const servicesRef = useRef<Services | null>(null);

  if (servicesRef.current === null) {
    const tokenProvider = createTokenProvider();
    const queryClient = createAppQueryClient();
    // The API client's 401 handler needs the router, which needs the API client — break the cycle with a
    // holder the closure reads lazily (the closure only fires on a real 401, long after both exist).
    const holder: { router?: AppRouter } = {};
    const sessionHolder: { sync?: SessionSync } = {};
    const api = createCoreApiClient(
      tokenProvider,
      () => {
        // A 401 on a non-auth route: tokens already cleared by the middleware; scorch the cache and bounce to login.
        queryClient.clear();
        void holder.router?.navigate({ to: "/login" });
      },
      // Web only: other tabs share this session and rotate its bearer, so a 401 may just mean "stale".
      isNative() ? undefined : (stale) => sessionHolder.sync?.reissue(stale) ?? Promise.resolve(null),
    );
    const sessionSync = isNative() ? undefined : createSessionSync(api, tokenProvider);
    sessionHolder.sync = sessionSync;
    const router = createAppRouter({
      auth: { isAuthenticated: () => tokenProvider.isAuthenticated() },
      api,
      queryClient,
    });
    holder.router = router;

    // Deep links (GATE-3). Capture the cold-launch `?to=<route>` entry NOW (before the router
    // mounts) so it parks until auth + bootstrap resolve; install the native universal-link / push-tap
    // listeners (a no-op on web). The parked route is flushed after `ready` (the effect below).
    const deepLink = createDeepLinkController();
    deepLink.handleInitial(window.location.search);
    void installNativeDeepLinkListeners(deepLink, { translatePush: translateCorePush });

    servicesRef.current = { tokenProvider, queryClient, api, router, deepLink, sessionSync };
  }

  const { tokenProvider, queryClient, api, router, deepLink, sessionSync } = servicesRef.current;

  // Boot reissue must fire EXACTLY ONCE. `POST /auth/reissue` rotates its single-use cookie, so
  // two concurrent calls race — the loser 401s and strands the session unauthenticated. React StrictMode
  // double-invokes this effect (and any future remount would too), so we memoize the in-flight promise in a
  // ref: both invocations await the SAME bootstrapSession, and only one reissue is ever sent.
  const bootstrapRef = useRef<Promise<boolean> | null>(null);
  useEffect(() => {
    bootstrapRef.current ??= bootstrapSession(api, tokenProvider, sessionSync);
    let active = true;
    void bootstrapRef.current.finally(() => {
      if (active) setReady(true);
    });
    return () => {
      active = false;
    };
  }, [api, tokenProvider, sessionSync]);

  // Cold-launch deep-link flush: once auth + bootstrap + the router are live, hand the controller the
  // router navigator — it immediately flushes any parked `?to=`/push tap and routes every subsequent tap. The
  // router's own beforeLoad guards then handle an unauthenticated target (→ /login?redirect=…), which is the
  // "park until auth resolves" contract for a route that needs a session.
  useEffect(() => {
    if (!ready) return;
    deepLink.activate((tapped) => {
      // A push names its notification's band (pushTarget.ts). Open it there — asking first when that would change
      // the active band — and never with the band parameter left in the URL.
      const { route, bandId } = takeNotificationBand(tapped);
      if (!bandId) return void router.navigate({ to: route });
      void (async () => {
        const me = await queryClient
          .fetchQuery({ queryKey: ["me"], queryFn: () => fetchMe(api), staleTime: 60_000 })
          .catch(() => undefined);
        const target = bandToSwitchTo(me, useActiveBand.getState().activeBandId, bandId, route);
        if (target) useNotificationBandPrompt.getState().ask(route, target);
        else void router.navigate({ to: route });
      })();
    });
  }, [ready, deepLink, router, queryClient, api]);

  // Live updates (native): every navigation looks for a newer bundle (throttled), and a move to another page — a
  // tab switch included — switches to one already downloaded, landing the reader on the page they asked for
  // (liveUpdate.ts). A change of query string alone never reloads.
  useEffect(
    () =>
      router.subscribe("onBeforeLoad", ({ fromLocation, toLocation }) => {
        if (fromLocation) onNavigation(fromLocation.pathname !== toLocation.pathname);
      }),
    [router],
  );

  // Fade the branded pre-hydration boot mask (#acLoader, apps/core/index.html) once the router mounts — it
  // snaps the fill to 100% and removes itself. Optional-call: it is a no-op in embed mode and absent until the
  // inline script has run.
  useEffect(() => {
    if (ready || outdated) window.acHideLoader?.();
  }, [ready, outdated]);

  // The API refused this client: nothing behind this can work, so the update screen replaces the whole app.
  if (outdated) return <ClientOutdatedScreen remedy={outdated} />;

  if (!ready) return <BootSplash />;

  return (
    <AppProvider value={{ api, tokenProvider }}>
      <QueryClientProvider client={queryClient}>
        {/* Face-ID gate: native + a held session stays locked until biometric unlock; inert on web.*/}
        <BiometricGate>
          <RouterProvider router={router} />
        </BiometricGate>
      </QueryClientProvider>
    </AppProvider>
  );
}

/** The cold-start boot screen — brand wordmark + spinner while the session resolves.*/
function BootSplash() {
  return (
    <div className={styles.splash} role="status" aria-live="polite">
      <div className={styles.brand}>
        <Wordmark size={32} />
      </div>
      <Spinner size={20} label="Starting CNTRL" />
    </div>
  );
}
